import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Buttons/Button";
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { getUserRolePermissions, getUsers } from '../../../services/userServicer';
import Select from 'react-select';
import { getControls } from "src/services/controls";
import { useForm } from "react-hook-form";
import { MdOutlineDelete, MdKeyboardArrowDown, MdKeyboardArrowRight, MdHourglassEmpty, MdCheckCircle, MdCancel, MdKeyboardArrowUp } from "react-icons/md";
import { createSurvey, fetchAllDataApi, getSurveyQuestionByQuarterByPage, updateQuestionApi } from "src/services/question"
import { useNavigate, useParams } from 'react-router-dom';
import i18n from "src/i18n";
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { CiExport, CiFilter } from "react-icons/ci";
import LoadingScreen from "../../../components/Loading/LoadingScreen";

const ExportSurvey = () => {
  const { t } = useTranslation();
  const { vendorId } = useParams();
  const { id } = useParams();
  const [notAllowed, setNotAllowed] = useState(false);
  const [questions, setQuestions] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [validationModal, setValidationModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isValidated, setIsValidated] = useState<{[key: string]: boolean}>({});

  // State for quarter and year
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedYear, setSelectedYear] = useState(2024);
  const [userPermissions, setUserPermissions] = useState<any>(null);

  // Column colors
  const columnColors = {
    QID: '#ADD8E6', // Light Blue for QID
    Category: '#fff', // Light Green for Category
    Question: '#fff', // Gold for Question
    Owner: '#fff', // Light Salmon for Owner
    RiskLevels: '#fff', // Tomato for Risk Levels
  };

  // Risk level colors
  const riskLevelColors = {
    Most: 'C00000',       // Red for "Most"
    Significant: 'FF0000', // Orange for "Significant"
    Moderate: 'FFC000',    // Yellow for "Moderate"
    Minimal: '00B050',     // Light Blue for "Minimal"
    Least: '92D050',       // Light Green for "Least"
    'N/A': 'FFFFFF',      // White for "N/A"
  };

  const riskLevelColorsForTable = {
    Most: '#C00000',       // Red for "Most"
    Significant: '#FF0000', // Orange for "Significant"
    Moderate: '#FFC000',    // Yellow for "Moderate"
    Minimal: '#00B050',     // Light Blue for "Minimal"
    Least: '#92D050',       // Light Green for "Least"
    'N/A': '#FFFFFF',      // White for "N/A"
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName]
    );
  };

  const toggleValidationModal = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setValidationModal(!validationModal);
  };

  const handleValidation = (validate: boolean) => {
    setIsValidated(prev => ({
      ...prev,
      [selectedCategory]: validate
    }));
    setValidationModal(false);
    toast.success(`Category "${selectedCategory}" has been ${validate ? 'validated' : 'invalidated'}`);
  };

  const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: black;
      color: white;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    tr:hover {
      background-color: #ddd;
    }
    .category-row {
      background-color: #c7c5c1e0;
      color: #44403f;
    font-size: 16px;
      cursor: pointer;
      font-weight: bolder;
    }
    .category-row:hover {
      background-color:#adaca9e0;
    }
  `;

  const ActionButton = styled.button`
    padding: 5px 10px;
    margin: 0 5px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:disabled {
      cursor: default;
      opacity: 1;
    }
    &:hover:not(:disabled) {
      opacity: 0.8;
    }
  `;

  const filterData = async (quarter = selectedQuarter, year = selectedYear, pageNum = page) => {
    setLoading(true);
    try {
      const response = await getSurveyQuestionByQuarterByPage(
        id,
        vendorId,
        quarter,
        year,
        pageNum,
        pageSize
      );
      
      if (response?.success) {
        setQuestions(response?.data?.result || []);
        setTotalPages(response?.data?.totalPages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error in filterData:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const groupQuestionsByCategory = () => {
    const grouped: { [key: string]: any[] } = {};
    questions.forEach((row: any) => {
      const categoryName = row?.question?.category?.name || 'Uncategorized';
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(row);
    });

    // Sort categories by createdAt
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const aCreatedAt = grouped[a][0]?.question?.category?.createdAt || 0;
      const bCreatedAt = grouped[b][0]?.question?.category?.createdAt || 0;
      return new Date(aCreatedAt).getTime() - new Date(bCreatedAt).getTime();
    });

    // Create a new object with sorted categories
    const sortedGrouped: { [key: string]: any[] } = {};
    sortedCategories.forEach(category => {
      // Sort questions within each category by createdAt
      sortedGrouped[category] = grouped[category].sort((a, b) => {
        const aCreatedAt = a?.question?.createdAt || 0;
        const bCreatedAt = b?.question?.createdAt || 0;
        return new Date(aCreatedAt).getTime() - new Date(bCreatedAt).getTime();
      });
    });

    return sortedGrouped;
  };

  useEffect(() => {
    filterData();
  }, [pageSize, page, vendorId]);

  useEffect(() => {
    fetchUserRolePermissions();
  }, []);

  const fetchUserRolePermissions = async () => {
    setLoading(true);
    try {
      const permissions = await getUserRolePermissions();
      if (permissions.success) {
        const userPagePermission = permissions.data.permissions.find((permission: any) => permission.page === "survey");
        if (userPagePermission) {
          setUserPermissions(userPagePermission?.permissions);
          setNotAllowed(!userPagePermission?.permissions.show);
          filterData();
        } else {
          setNotAllowed(true);
        }
      } else {
        setNotAllowed(true);
      }
    } catch (error: any) {
      toast.error(t("permissions.Error_fetching_user_role_permissions") + ":", error);
    }
    setLoading(false);
  };

  const exportToExcel = async() => {
    setLoading(true);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Inherent Risk Profile');

    const response = await getSurveyQuestionByQuarterByPage(
      id,
      vendorId,
      selectedQuarter,
      selectedYear,
      0,
      500
    );
    
    if(response.success){
      const data = response?.data?.result;
      const data2 = data?.map((row:any,index:any) => ({
        qid: index+1,
        category: row?.question?.category?.name,
        question: row?.question?.question_en,
        owner: row?.user?.name,
        riskLevel: row?.answer,
        validated: isValidated[row?.question?.category?.name] ? 'Yes' : 'No'
      }));
   
    // Add headers with styling
    worksheet.columns = [
      { header: 'QID', key: 'qid', width: 10, style: { alignment: { vertical: 'middle', horizontal: 'center', wrapText: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFADD8E6' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } } },
      { header: 'Category', key: 'category', width: 30, style: { alignment: { vertical: 'middle', horizontal: 'center', wrapText: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } } },
      { header: 'Question', key: 'question', width: 80, style: { alignment: { vertical: 'middle', horizontal: 'left', wrapText: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } } },
        { header: 'Response', key: 'riskLevel', width: 25, style: { alignment: { vertical: 'middle', horizontal: 'center', wrapText: true }, fill: { type: 'pattern', pattern: 'solid' , fgColor: { argb: 'FFFFFF' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } } },
        { header: 'Validated', key: 'validated', width: 15, style: { alignment: { vertical: 'middle', horizontal: 'center', wrapText: true }, fill: { type: 'pattern', pattern: 'solid' , fgColor: { argb: 'FFFFFF' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } } }
    ];

    // Set header row height
    worksheet.getRow(1).height = 30;

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell:any) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '000000' }, // Black background
      };
      cell.font = {
        color: { argb: 'FFFFFF' }, // White text
        bold: true,
      };
    });

    // Add data rows and style the "Risk Levels" column
    data2.forEach((row:any) => {
      const newRow = worksheet.addRow(row);
      const riskLevelCell = newRow.getCell('riskLevel');
      riskLevelCell.fill = {
        type: 'pattern',
        pattern: 'solid',
          fgColor: { argb: riskLevelColors[row.riskLevel as keyof typeof riskLevelColors] || 'FFFFFF' },
      };
    });
  
    // Save the file
    workbook.xlsx.writeBuffer()?.then((buffer:any) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, 'InherentRiskProfile.xlsx');
    });
    } else {
      toast.error(response.message);
    }
    setLoading(false);
  };

  // Helper to extract industry value from questions
  const getIndustryValue = (questions: any[]) => {
    const industryQ = questions.find(
      (row: any) => row?.question?.question_en?.toLowerCase()?.includes('what is the industry')
    );
    return industryQ?.answer || '';
  };

  const [webhookLoading, setWebhookLoading] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<string | null>(null);
  const [validationResultOpen, setValidationResultOpen] = useState(false);

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }
  
  const groupedQuestions = groupQuestionsByCategory();
  const allQuestions = questions; // for industry extraction
  const industryValue = getIndustryValue(allQuestions);

  // Webhook handler
  const handlePendingValidation = async (category: string, categoryQuestions: any[]) => {
    setWebhookLoading(category); // Start loading
    try {
      const domain = categoryQuestions[0]?.question?.controls[0]?.control?.name || '';
      const claims = categoryQuestions.map((q: any) => ({
        question: q?.question?.question_en.trim(),
        answer: q?.answer.trim()
      }));
      const body = {
        domain: 'cbe.org.eg',
        industry: industryValue.trim().toLowerCase(),
        factor: {
          name: category.trim().toLowerCase(),
          claims
        }
      };
      let response = await fetch('http://38.170.241.78:5678/webhook/a3898940-13e2-4c92-9f48-df8ad8e12593', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      let validationValue='';
      if (Array.isArray(data) && data[0]?.output?.factor_trust === true) {
        setValidationResult('valid');
        setValidationResultOpen(true);
        validationValue = 'valid';
      } else {
        
        setValidationResultOpen(true);
        validationValue = 'not valid (' + data[0]?.output?.reason + ')';
        setValidationResult(validationValue);
      }
      setWebhookLoading(null); // End loading
      // Update each question in the category with its own reason
      setQuestions((prevQuestions: any[]) =>
        prevQuestions.map((q: any) => {
          const matchIdx = categoryQuestions.findIndex((cq: any) => cq.question.id === q.question.id);
          if (matchIdx !== -1) {
            const reason = Array.isArray(data) && data[matchIdx]?.output?.reason ? data[matchIdx].output.reason : null;
            // Update backend
            updateQuestionApi(q.question.id, { validation: validationValue});
            // Update local state
            return { ...q, question: { ...q.question, validation: validationValue } };
          }
          return q;
        })
      );
    } catch (err) {
      setWebhookLoading(null); // End loading
      setValidationResult('not_valid');
      setValidationResultOpen(true);
      toast.error('Webhook call failed');
    }
  };

  // Helper to get category validation status
  const getCategoryValidationStatus = (categoryQuestions: any[]) => {
    const validations = categoryQuestions.map(q => q.question.validation);
    if (validations.length > 0 && validations.every(v => v === 'valid')) return 'valid';
    if (validations.length > 0 && validations.some(v => v?.includes('not valid'))) return validations[0];
    return 'pending';
  };

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem>{t("surveys.Surveys")}</BreadcrumbItem>
        <BreadcrumbItem active style={{ paddingRight: '10px' }}>
          {t("Surveys Validation")}
        </BreadcrumbItem>
      </Breadcrumb>

      <h5 className="mb-lg">{t('Surveys Validation')}</h5>
     
      {!notAllowed && !loading && (
        <div className="container_info">
        <div className="d-flex flex-wrap mb-3">
            <div className="flex-grow-1" style={{ flexBasis: 'calc(20% - 10px)', margin: '10px', width: '100%' }}>
              <button style={{float:'right'}} className="submit-button5 mr-2 ml-2" onClick={exportToExcel}>
                <CiExport /> Export to Excel
              </button>
          </div>
        </div>

        <div>
          <Table>
            <thead>
              <tr style={{ backgroundColor: 'black', color: 'white' }}>
                <th>Category/QID</th>
                <th>Question</th>
                <th>Response</th>
                <th style={{textAlign:'center'}}>Validation</th>
              </tr>
            </thead>
            <tbody>
                {Object.entries(groupedQuestions).map(([category, categoryQuestions], catIdx) => {
                  const categoryStatus = getCategoryValidationStatus(categoryQuestions);
                  const isIntroduction = category.trim().toLowerCase() === 'introduction';
                  return (
                    <React.Fragment key={category}>
                      <tr className="category-row" style={{padding:'4px'}}>
                        <td colSpan={4} onClick={() => toggleCategory(category)}>
                          <div style={{ display: 'flex',width:'100%' }}>
                            <div style={{ width: isIntroduction ? '100%' : '80%' }}>
                              {expandedCategories?.includes(category) ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
                              <span style={{marginLeft:'10px',}}>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                            </div>
                            {!isIntroduction && (
                              <div style={{ width: '20%' ,textAlign:'center'}}>
                                <ActionButton
                                  onClick={() => handlePendingValidation(category, categoryQuestions)}
                                  disabled={categoryStatus !== 'pending' || webhookLoading === category}
                                  style={{
                                    marginLeft: '20px',
                                    borderRadius:'5%',
                                    color: '#fff',
                                    backgroundColor: categoryStatus === 'valid' ? '#28a745'
                                      : categoryStatus?.includes('not valid') ? '#dc3545'
                                      : '#ffc107',
                                    cursor: categoryStatus === 'pending' && webhookLoading !== category ? 'pointer' : 'default'
                                  }}
                                >
                                  {webhookLoading === category ? 'Loading...' :
                                    categoryStatus === 'valid' ? <><MdCheckCircle /> Validated</>
                                    : categoryStatus?.includes('understatement') ? <><MdCancel /> {categoryStatus}</>
                                    : categoryStatus === 'pending' ? <><MdHourglassEmpty /> Pending</> 
                                    : categoryStatus}
                                </ActionButton>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedCategories?.includes(category) && categoryQuestions.map((row: any, index: number) => (
                        <tr key={`${category}-${index}`} style={{padding:'4px'}}>
                          <td style={{ width: '10%', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ width: '60%' }}>{row?.question?.question_en}</td>
                          <td style={{ width: '20%', textAlign: 'center' }}>{row?.answer}</td>
                          <td style={{ width: '10%', textAlign: 'center' }}>
                            {category.trim().toLowerCase() === 'introduction'
                              ? null
                              : row.question.validation === 'valid' ? <MdCheckCircle color='#28a745' size={24} />
                                : row.question.validation?.includes('not valid') ? <MdCancel color='#dc3545' size={24} />
                                : <MdHourglassEmpty color='#ffc107' size={24} />}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </Table>
          </div>
          
          <div className="pagination-controls" style={{marginTop:'50px'}}>
                <button
                  className="btn btn-warning btn-sm text-white"
                  onClick={() => {
                    setPage(0);
                    filterData(selectedQuarter, selectedYear, 0);
                  }}
              disabled={page === 0}
                >
                  {"<<"}
                </button>{" "}
                <button
                  className="btn btn-warning btn-sm text-white"
                  onClick={() => {
                    const newPage = Math.max(page - 1, 0);
                    setPage(newPage);
                    filterData(selectedQuarter, selectedYear, newPage);
                  }}
                  disabled={page === 0}
                >
                  {"<"}
                </button>{" "}
                <button
                  className="btn btn-warning btn-sm text-white"
                  onClick={() => {
                    const newPage = Math.min(page + 1, totalPages - 1);
                    setPage(newPage);
                    filterData(selectedQuarter, selectedYear, newPage);
                  }}
              disabled={page >= totalPages - 1}
                >
                  {">"}
                </button>{" "}
                <button
                  className="btn btn-warning btn-sm text-white"
                  onClick={() => {
                    const newPage = totalPages - 1;
                    setPage(newPage);
                    filterData(selectedQuarter, selectedYear, newPage);
                  }}
              disabled={page >= totalPages - 1}
                >
                  {">>"}
                </button>{" "}
                <span>
              page {page + 1} to {totalPages}
                </span>
              </div>
        </div>
      )}

      {notAllowed && (
        <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
          </div>
        </div>
      )}

      <Modal isOpen={validationModal} toggle={() => setValidationModal(false)}>
        <ModalHeader toggle={() => setValidationModal(false)}>
          Validate : {selectedCategory}
        </ModalHeader>
        <ModalBody>
          Are you sure you want to {isValidated[selectedCategory] ? 'invalidate' : 'validate'} this category?
        </ModalBody>
        <ModalFooter>
         
          <button className="submit-button5 mr-2 ml-2" onClick={() => setValidationModal(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={validationResultOpen} toggle={() => setValidationResultOpen(false)}>
        <ModalBody style={{ textAlign: 'center', padding: '40px' }}>
          {validationResult === 'valid' ? (
            <div>
              <MdCheckCircle size={100} color="#28a745" />
              <h2 style={{ color: '#28a745', marginTop: 20 }}>Valid</h2>
            </div>
          ) : validationResult?.includes('not valid') ? (
            <div>
              <MdCancel size={100} color="#dc3545" />
              <h2 style={{ color: '#dc3545', marginTop: 20 }}>{validationResult}</h2>
            </div>
          ) : null}
          <button className="submit-button5 mr-2 ml-2" style={{ marginTop: 30 }} onClick={() => setValidationResultOpen(false)}>
            Close
          </button>
        </ModalBody>
      </Modal>

      {webhookLoading && (
        <Modal isOpen centered>
          <ModalBody style={{ textAlign: 'center', padding: '40px' }}>
            <div>
              <MdHourglassEmpty size={80} color="#ffc107" />
              <h3 style={{ marginTop: 20 }}>Validating...</h3>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default ExportSurvey;